import type { Pinia } from 'pinia'
import { setActivePinia } from 'pinia'

import { useUploadRunner } from '../composables/useUploadRunner'
import { uploadImageRecord, uploadImageRecordsBatch } from '../services/uploadService'
import { getWidgetConfig } from './widgetConfig'
import { rehydrateQueueFromDb } from '../services/rehydrateQueueFromDb'
import { useCartStore } from '../store/cart'
import { useProjectStore } from '../store/useProjectStore'
import { useImageDB, type ImageRecord } from '../utils/imageDb'
import { readImageDimensions } from '../utils/imageOps'
import { buildMaxCropForRecord } from '../utils/cropPreset'
import { emitUploadEvent } from '../utils/uploadBus'

export type UploadsRunnerOptions = {
    pinia: Pinia
    pollMs?: number
    concurrency?: number
    maxTries?: number
    // falls du projectCode von außen übergeben willst:
    projectCode?: string
}


let _runnerPromise: Promise<Awaited<ReturnType<typeof initUploadsRunner>>> | null = null
let _runnerControls: Awaited<ReturnType<typeof useUploadRunner>> | null = null

function hasStableStoredCrop(crop: any): boolean {
    if (!crop || typeof crop !== 'object') return false
    const width = Math.max(0, Number(crop?.w ?? crop?.width ?? 0) || 0)
    const height = Math.max(0, Number(crop?.h ?? crop?.height ?? 0) || 0)
    if (width <= 1 || height <= 1) return false
    const sourceWidth = Math.max(0, Number(crop?.sourceWidth ?? 0) || 0)
    const sourceHeight = Math.max(0, Number(crop?.sourceHeight ?? 0) || 0)
    const normalizedWidth = Math.max(0, Number(crop?.nw ?? 0) || 0)
    const normalizedHeight = Math.max(0, Number(crop?.nh ?? 0) || 0)
    return (sourceWidth > 1 && sourceHeight > 1) || (normalizedWidth > 0 && normalizedHeight > 0)
}

async function withTimeout<T>(work: Promise<T>, ms: number, fallback: T): Promise<T> {
    let timer: ReturnType<typeof setTimeout> | null = null
    try {
        return await Promise.race<T>([
            work,
            new Promise<T>((resolve) => {
                timer = setTimeout(() => resolve(fallback), Math.max(1, ms))
            }),
        ])
    } finally {
        if (timer) clearTimeout(timer)
    }
}

/** After upload: ensure image dimensions are stored and crop is set to max-fit. */
async function applyDimsAndCropAfterUpload(rec: ImageRecord): Promise<void> {
    const db = useImageDB('imageDB')

    // Get or read dims
    let width = Number((rec as any).width || 0)
    let height = Number((rec as any).height || 0)

    if (width <= 1 || height <= 1) {
        // Read dims from the local blob (still in memory right after upload)
        const blob = (rec as any).workingBlob || (rec as any).originalBlob
        if (!blob) return
        const dims = await readImageDimensions(blob).catch(() => null)
        if (!dims || dims.width <= 1) return
        width = dims.width
        height = dims.height
        await db.update(rec.id, { width, height, editorWidth: width, editorHeight: height } as any)
        emitUploadEvent({ type: 'upload:patched', id: rec.id, patch: { width, height, editorWidth: width, editorHeight: height } })
    }

    // Re-read latest so we have any recordPatch updates (mime, size, etc.)
    const latest = await db.get(rec.id)
    if (!latest) return

    const existingEdits: any = (latest as any).edits || {}
    const preset = existingEdits.preset
    const existingCrop = existingEdits.crop

    // Skip if crop is already valid (user/stored crop with source or normalized dimensions)
    if (hasStableStoredCrop(existingCrop)) return

    // buildMaxCropForRecord returns full-image crop when size is null — always valid
    const recWithDims: any = { ...latest, width, height, editorWidth: width, editorHeight: height }
    const freshCrop = buildMaxCropForRecord(recWithDims, preset?.size ?? null)
    if (!freshCrop || freshCrop.sourceWidth <= 1) return

    // Write only the crop to DB (deep-merge via db.update which reads + merges edits)
    const latest2 = await db.get(rec.id)
    if (!latest2) return
    const mergedEdits = { ...((latest2 as any).edits || {}), crop: freshCrop }
    await db.update(rec.id, { edits: mergedEdits } as any)
    // Emit only the crop — safeMergePatch in drawer will merge into current edits without overwriting preset/size
    emitUploadEvent({ type: 'upload:patched', id: rec.id, patch: { edits: { crop: freshCrop } } })
}
const pendingCartSyncRecords = new Map<string, any>()
let pendingCartSyncTimer: ReturnType<typeof setTimeout> | null = null
let _scheduledTickTimer: ReturnType<typeof setTimeout> | null = null

export function getUploadsRunner() {
    return _runnerControls
}

export function scheduleUploadsRunnerTick(delayMs = 180) {
    if (_scheduledTickTimer) clearTimeout(_scheduledTickTimer)
    _scheduledTickTimer = setTimeout(() => {
        _scheduledTickTimer = null
        void _runnerControls?.tick?.()
    }, Math.max(0, delayMs))
}

export function initUploadsRunnerOnce(opts: UploadsRunnerOptions) {
    if (_runnerPromise) return _runnerPromise
    _runnerPromise = initUploadsRunner(opts)
    return _runnerPromise
}


/**
 * Startet den Upload-Autorunner (Widget/Client only).
 * Gibt eine stop()-Funktion zurück, damit du beim unmount sauber aufräumen kannst.
 */
export async function initUploadsRunner(opts: UploadsRunnerOptions) {
    const isMobileClient =
        typeof navigator !== 'undefined'
        ? /Android|iPhone|iPad|iPod/i.test(navigator.userAgent || '')
        : false
    const hardwareThreads =
        typeof navigator !== 'undefined'
        ? Math.max(1, Number(navigator.hardwareConcurrency || 4) || 4)
        : 4
    const {
        pinia,
        pollMs = 1200,
        concurrency = isMobileClient
            ? 1
            : Math.max(4, Math.min(6, Math.ceil(hardwareThreads / 2))),
        maxTries = 4,
        projectCode,
    } = opts

    // wichtig: im Widget gibt es keinen "global active pinia" wie in Nuxt
    setActivePinia(pinia)

    const db = useImageDB('imageDB')

    // optional rehydrate
    if (typeof rehydrateQueueFromDb === 'function') {
        await withTimeout(Promise.resolve(rehydrateQueueFromDb()).catch(() => undefined), 1500, undefined)
    }

    const project = useProjectStore()
    const cart = useCartStore()
    const cfg = getWidgetConfig() as any
    const directPresignBatchSize = Math.max(1, Math.min(500, Number(cfg?.editorDirectPresignBatchSize || 200) || 200))

    let resolvedCode = String(projectCode || project.projectCode.value || '').trim() || null
    async function ensureCode() {
        if (resolvedCode) return resolvedCode
        const next = await withTimeout(
            Promise.resolve(project.ensureProject?.()).catch(() => null),
            1500,
            null,
        )
        resolvedCode = String(next || project.projectCode.value || '').trim() || null
        return resolvedCode
    }

    async function flushPendingCartSync() {
        if (pendingCartSyncTimer) {
            clearTimeout(pendingCartSyncTimer)
            pendingCartSyncTimer = null
        }
        const records = [...pendingCartSyncRecords.values()]
        pendingCartSyncRecords.clear()
        if (!records.length) return
        const code = await ensureCode()
        if (!code) return
        await cart.reconcileProjectImages(records as any[], code).catch(() => {})
    }

    function queuePendingCartSync(rec: any) {
        const id = String(rec?.id || '').trim()
        if (!id) return
        pendingCartSyncRecords.set(id, rec)
        if (pendingCartSyncTimer) return
        pendingCartSyncTimer = setTimeout(() => {
            void flushPendingCartSync()
        }, 500)
    }

    const runner = useUploadRunner(
        async (rec) => {
            const code = await ensureCode()
            if (!code) throw new Error('projectCode missing')
            const result = await uploadImageRecord(rec, code)
            return { remoteUrl: result.remoteUrl }
        },
        {
            concurrency,
            maxTries,
            batchSize: directPresignBatchSize,
            batchParallelJobs: 1,
            batchUploadFn: async (recs) => {
                const code = await ensureCode()
                if (!code) throw new Error('projectCode missing')
                return uploadImageRecordsBatch(recs, code)
            },
            onUploaded: async (rec) => {
                // Persist dimensions + crop before downstream syncs pick the record up.
                await applyDimsAndCropAfterUpload(rec).catch(() => {})

                const code = await ensureCode()
                if (!code) return
                const exists = await cart.hasImageInCart(String((rec as any)?.id || ''), code).catch(() => false)
                if (!exists) return
                const latest = await db.get(rec.id).catch(() => null)
                queuePendingCartSync((latest || rec) as any)
            },
        }
    )
    _runnerControls = runner

    runner.startAuto(pollMs)

    const resumeUploadFlow = () => {
        runner.resume?.()
        void runner.tick?.()
    }
    const pauseUploadFlow = () => {
        runner.pause?.()
    }

    const handleVisibilityChange = () => {
        if (typeof document === 'undefined') return
        if (document.visibilityState === 'hidden') {
            pauseUploadFlow()
            return
        }
        resumeUploadFlow()
    }

    const handlePageShow = () => {
        resumeUploadFlow()
    }

    const handleOnline = () => {
        resumeUploadFlow()
    }

    if (typeof document !== 'undefined') {
        document.addEventListener('visibilitychange', handleVisibilityChange)
    }
    if (typeof window !== 'undefined') {
        window.addEventListener('pageshow', handlePageShow)
        window.addEventListener('online', handleOnline)
        window.addEventListener('focus', handlePageShow)
    }

    return {
        stop() {
            runner.stopAuto?.()
            if (pendingCartSyncTimer) {
                clearTimeout(pendingCartSyncTimer)
                pendingCartSyncTimer = null
            }
            if (_scheduledTickTimer) {
                clearTimeout(_scheduledTickTimer)
                _scheduledTickTimer = null
            }
            pendingCartSyncRecords.clear()
            if (typeof document !== 'undefined') {
                document.removeEventListener('visibilitychange', handleVisibilityChange)
            }
            if (typeof window !== 'undefined') {
                window.removeEventListener('pageshow', handlePageShow)
                window.removeEventListener('online', handleOnline)
                window.removeEventListener('focus', handlePageShow)
            }
        },
        projectCode: resolvedCode,
        runner,
    }
}
